import React from 'react';
import { gradeLabel, subjectLabel } from '../../utils/catalogue';
import { moderatorName } from '../../utils/useModerators';

export const formatDate = (value) => {
  if (!value) return '—';
  if (typeof value === 'string') return new Date(value).toLocaleDateString();
  return value.toDate ? value.toDate().toLocaleDateString() : '—';
};

export const SortIcon = ({ sortConfig, columnKey }) => {
  if (sortConfig?.key !== columnKey) return <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1 inline opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
  return sortConfig.direction === 'ascending'
    ? <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1 inline text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
    : <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1 inline text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>;
};

/** Subject and grade, plus (for administrators) who covers that scope. */
export const ScopeCell = ({ pdf, showAssignment, assignedTo }) => {
  const assigned = showAssignment && assignedTo ? assignedTo(pdf) : [];
  return (
    <div className="text-xs">
      <div className="text-gray-700">{subjectLabel(pdf.subject) || '—'}</div>
      <div className="text-gray-400">{gradeLabel(pdf.grade)}</div>
      {showAssignment && (
        assigned.length > 0 ? (
          <div className="mt-1 text-[11px] text-gray-400" title={assigned.map(moderatorName).join(', ')}>
            {assigned.length === 1 ? moderatorName(assigned[0]) : `${assigned.length} moderators`}
          </div>
        ) : (
          <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">Unassigned</span>
        )
      )}
    </div>
  );
};

/** Title link with optional "New" badge, theme line and rename control. */
export const TitleCell = ({ pdf, getPublicUrl, isNew, onRename }) => (
  <div>
    <div className="flex items-start gap-1.5">
      <a
        href={getPublicUrl(pdf.file_url)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-600 hover:text-brand-700 hover:underline font-medium transition-colors"
      >
        {pdf.title || 'No Title'}
      </a>
      {isNew && isNew(pdf) && (
        <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">New</span>
      )}
    </div>
    {pdf.theme && <div className="text-xs text-gray-400 mt-0.5">Theme: {pdf.theme}</div>}
    {onRename && (
      <button
        type="button"
        onClick={() => onRename(pdf)}
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 hover:text-brand-600 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
        Rename{pdf.renameHistory?.length ? ` · ${pdf.renameHistory.length} change${pdf.renameHistory.length === 1 ? '' : 's'}` : ''}
      </button>
    )}
  </div>
);

export const Pagination = ({ page, pageSize, total, totalPages, setPage }) => (
  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
    <span className="text-xs text-gray-400">Showing {total ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, total)} of {total}</span>
    <div className="flex gap-1">
      <button
        className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={page === 1}
      >‹ Prev</button>
      <button
        className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={page === totalPages}
      >Next ›</button>
    </div>
  </div>
);

export const EmptyState = ({ title, message }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-400 mb-3">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    </div>
    <p className="text-sm font-medium text-gray-500">{title}</p>
    <p className="text-xs text-gray-400 mt-0.5">{message}</p>
  </div>
);

export const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
);
